import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Clock, Eye, Loader2, FileText } from "lucide-react";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`).then((r) => {
      setPost(r.data);
      // related: same tags
      if (r.data?.tags?.length) {
        api.get(`/blog?tag=${encodeURIComponent(r.data.tags[0])}&published=true&page_size=4`).then((rr) => {
          setRelated((rr.data.items || []).filter((p) => p.slug !== slug).slice(0, 3));
        });
      }
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-20 text-center text-stone-400"><Loader2 className="mx-auto animate-spin" size={36}/></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-20 text-center"><FileText size={48} className="mx-auto text-stone-300"/><p className="font-display text-2xl mt-3">Post não encontrado</p><Link to="/blog" className="text-blue-900 mt-3 inline-block">← Voltar ao blog</Link></div>;

  return (
    <div className="bg-stone-50 min-h-screen">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/blog" className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center gap-1" data-testid="blog-back"><ArrowLeft size={14}/> Voltar ao blog</Link>
        <header className="mt-6">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {post.tags?.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 font-bold uppercase">{t}</span>)}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-stone-900 leading-tight" data-testid="post-title">{post.title}</h1>
          <p className="text-stone-600 text-lg mt-3">{post.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-stone-500 mt-4 pb-4 border-b border-stone-200">
            <span>{post.author_name}</span><span>·</span>
            <span className="flex items-center gap-1"><Clock size={12}/> {fmtDate(post.created_at)}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Eye size={12}/> {post.views || 0} views</span>
          </div>
        </header>

        {post.cover_url && (
          <div className="aspect-[16/9] bg-stone-100 rounded-2xl overflow-hidden my-6">
            <img src={post.cover_url} alt={post.title} className="w-full h-full object-cover"/>
          </div>
        )}

        <div className="prose prose-stone max-w-none prose-headings:font-display prose-h2:text-2xl prose-h3:text-xl prose-a:text-blue-900 prose-img:rounded-xl" data-testid="post-content" dangerouslySetInnerHTML={{__html: post.content_html}}/>

        {related.length > 0 && (
          <section className="mt-12 pt-8 border-t border-stone-200">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-4">Leia também</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-xl bg-white border border-stone-200 overflow-hidden hover:shadow-md transition-all">
                  <div className="aspect-[16/9] bg-stone-100">{p.cover_url && <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover"/>}</div>
                  <h3 className="p-3 font-display text-sm font-bold text-stone-900 line-clamp-2 group-hover:text-blue-900">{p.title}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}
